import React, { useContext } from 'react';
import './NotificationSection.scss';
import { useConfigService, ExperienceContext } from '@thd-nucleus/experience-context';
import { Image, Loader } from '@thd-olt-component-react/core-ui';
import { handleTileAnalytics } from '../../common/utils/AnalyticsUtils';
import * as Constants from '../../core/Constants';
import { floorNumberWithCommas } from '../../common/utils/CurrencyUtils';
import { ProgressTracker } from '../../common/ProgressTracker/ProgressTracker';
import {
  getAccrualData,
  getHostCURL,
  getImageURL,
  PerksUrl,
  isObjectEmpty,
} from '../../common/utils/cardUtils';

const analytics = Constants.analytics.NOTIFICATION;
const ProgressPerk = ({ perkSummary }) => {
  const perkData = getAccrualData(perkSummary);
  const amountRequired = perkData?.spendForNextPerk ? perkData?.spendForNextPerk : 0;
  const amount = floorNumberWithCommas(amountRequired, 0);
  const percentage = parseInt(perkSummary?.perkCurrentTierProgressPercentage, 10);
  return (
    <div className="notification-block">
      <a
        className="link-url"
        href={PerksUrl()}
        rel=" noreferrer"
        onClick={() => handleTileAnalytics(analytics.PREFIX, analytics.perkStatus)}
      >
        <div>
          <div className="notification-tracker">
            <ProgressTracker percentage={percentage} />
          </div>
        </div>
        <div className="notification-label">${amount} Until Your Next Perk</div>
        <div className="link-label">
          <Image
            width="10px"
            height="16px"
            src={getImageURL('caret-small.svg')}
            alt="Orangee Arrow"
            className="link-arrow"
          />
        </div>
      </a>
    </div>
  );
};

const UnlockPerk = ({ perkSummary }) => {
  const { unclaimedPerksCount } = perkSummary;
  return (
    <div className="notification-block">
      <a
        className="link-url"
        href={PerksUrl()}
        rel=" noreferrer"
        onClick={() => handleTileAnalytics(analytics.PREFIX, analytics.availablePerks)}
      >
        <div className="notification-image">
          <Image
            src={getImageURL('badge-unlocked.svg')}
            width="28px"
            height="28px"
            alt="paintImage"
          />
        </div>
        <div className="notification-label">New Unlocked Perk</div>
        <div className="circle-count-main">
          <span className="circle-count">{unclaimedPerksCount || 0}</span>
        </div>
      </a>
    </div>
  );
};

const AvailablePerks = ({ availablePerksCount }) => {
  const { availablePerks } = availablePerksCount;
  return (
    <div className="notification-block">
      <a
        className="link-url"
        href={PerksUrl()}
        rel=" noreferrer"
        onClick={() => handleTileAnalytics(analytics.PREFIX, analytics.availablePerks)}
      >
        <div className="notification-image">
          <Image
            // className="notification-image"
            src={getImageURL('Pro-Xtra_Perk_Icon-whiteFill.svg')} // pro-extra-perk.svg
            width="25px"
            height="30px"
            alt="perks"
          />
        </div>
        <div className="notification-label">Your Available Perks</div>
        <div className="circle-count-main">
          <span className="circle-count">{availablePerks || 0}</span>
        </div>
      </a>
    </div>
  );
};

const WellDoneCard = ({ userProfile }) => {
  const { firstName } = userProfile.name;
  return (
    <div className="notification-block">
      <a
        className="link-url"
        href={PerksUrl()}
        rel=" noreferrer"
        onClick={() => handleTileAnalytics(analytics.PREFIX, analytics.availablePerks)}
      >
        <div>
          <Image
            // className="notification-image"
            src={getImageURL('Pro-Xtra_Perk_Icon-whiteFill.svg')}
            width="25px"
            height="30px"
            alt="paintImage"
          />
        </div>
        <div className="notification-label">Well Done{firstName && `, ${firstName}`}!</div>
        <div className="link-label-text ">View Perks</div>
      </a>
    </div>
  );
};

const PerkExpireLabel = ({ availablePerksCount }) => {
  const { expiringPerks, daysLeft } = availablePerksCount;
  let expireLabel;
  if (expiringPerks > 1) {
    expireLabel = `Perks Expire in ${daysLeft || 0} Days`;
  } else {
    expireLabel = `Perk Expiring In ${daysLeft || 0} Days`;
  }
  return expireLabel;
};

const PerkExpire = ({ availablePerksCount }) => {
  return (
    <div className="notification-expire-error ">
      <a
        className="link-url"
        href={PerksUrl()}
        rel=" noreferrer"
        onClick={() => handleTileAnalytics(analytics.PREFIX, analytics.availablePerks)}
      >
        <div className="notification-image">
          <Image
            // className="notification-image"
            src={getImageURL('warning-yellow.svg')}
            width="25px"
            height="30px"
            alt="paintImage"
          />
        </div>
        <div className="notification-label">
          {' '}
          <PerkExpireLabel availablePerksCount={availablePerksCount} />
        </div>
        <div className="link-label-text">Redeem</div>
      </a>
    </div>
  );
};

const PerkExclusive = ({ availablePerksCount }) => {
  const { availableOffers } = availablePerksCount;
  return (
    <div className="notification-block">
      <a
        className="link-url"
        href={getHostCURL('/myaccount/proxtraprogramdetails')}
        rel=" noreferrer"
        onClick={() => handleTileAnalytics(analytics.PREFIX, analytics.availableOffers)}
      >
        <div className="notification-image">
          <Image
            // className="notification-image"
            src={getImageURL('ProTiers/cardIcon-PersonalOffers.svg')}
            width="22px"
            height="25px"
            alt="offersImage"
          />
        </div>
        <div className="notification-label">Pro Xtra Exclusive Offers</div>
        <div className="circle-count-main">
          <span className="circle-count">{availableOffers || 0}</span>
        </div>
      </a>
    </div>
  );
};

const PreferredUnlocked = () => {
  const isProxtraPricingEnabled = useConfigService('ENABLE_NEW_PROXTRA_PRICING_PAGE');
  const pricingPrefix = isProxtraPricingEnabled === 1 ? '/myaccount' : '/b2b/account/view';
  return (
    <div className="notification-block">
      <a
        className="link-url"
        href={getHostCURL(`${pricingPrefix}/proXtraPricing`)}
        rel=" noreferrer"
        onClick={() => handleTileAnalytics(analytics.PREFIX, analytics.pricingUnlocked)}
      >
        <div className="notification-image">
          <Image
            // className="notification-image"
            src={getImageURL('ProTiers/cardIcon-PreferredPricing.svg')}
            width="25px"
            // height="25.45px"
            alt="paintImage"
          />
        </div>
        <div className="notification-label">Preferred Pricing Unlocked</div>
        <div className="link-label">
          <Image
            width="10px"
            height="16px"
            src={getImageURL('caret-small.svg')}
            alt="Orangee Arrow"
            className="link-arrow"
          />
        </div>
      </a>
    </div>
  );
};

const ProCreditCard = ({ perkSummary }) => {
  const perkData = perkSummary ? getAccrualData(perkSummary) : null;
  const amountRequired = perkData?.spendForNextPerkWithPxcc
    ? perkData?.spendForNextPerkWithPxcc
    : 0;
  const amount = floorNumberWithCommas(amountRequired, 0);
  const factor = perkSummary?.activePxccFactor ? perkSummary?.activePxccFactor : 4;
  return (
    <div className="notification-block">
      <a
        className="link-url"
        href={PerksUrl()}
        rel=" noreferrer"
        onClick={() => handleTileAnalytics(analytics.PREFIX, analytics.perkStatus)}
      >
        <div className="notification-image">
          <div className="notification__inner-circle">{factor}X</div>
          <Image
            // className="notification-image"
            src={getImageURL('cardPLCCX.png')}
            width="46px"
            height="28px"
            alt="paintImage"
          />
        </div>
        <div className="notification-label">
          <div className="notification__labelPLCC">${amount} Until Next Perk</div>
          <div className="notification__labelPLCC">with Pro Xtra Credit Card</div>
        </div>
        <div className="link-label">
          <Image
            width="10px"
            height="16px"
            src={getImageURL('caret-small.svg')}
            alt="Orangee Arrow"
            className="link-arrow"
          />
        </div>
      </a>
    </div>
  );
};

const PaintMemberLabel = ({ currentPaintLevel, paintCurrentYearSpend }) => {
  let paintLevel;
  const paintText = paintCurrentYearSpend > 900
    ? 'Close to unlocking paint rewards'
    : 'Learn more about Paint Rewards';
  if (currentPaintLevel === 'Bronze') {
    paintLevel = 'Save 10% on paint products';
  } else if (currentPaintLevel === 'Silver') {
    paintLevel = 'Save 15% on paint products';
  } else if (currentPaintLevel === 'Gold') {
    paintLevel = 'Save 20% on paint products';
  } else {
    paintLevel = paintText;
  }
  return paintLevel;
};

const PaintMemberProducts = ({ currentPaintLevel, paintCurrentYearSpend }) => {
  const isProgramDetailsEnabled = useConfigService('ENABLE_NEW_PROGRAM_DETAILS_PAGE');
  const programDetailsPrefix = isProgramDetailsEnabled === 1 ? '/myaccount' : '/b2b/account/view';
  let paintImage;
  if (currentPaintLevel === 'Bronze') {
    paintImage = 'ProTiers/cardIcon-PaintRewards-Bronze.svg';
  } else if (currentPaintLevel === 'Silver') {
    paintImage = 'ProTiers/cardIcon-PaintRewards-Silver.svg';
  } else if (currentPaintLevel === 'Gold') {
    paintImage = 'ProTiers/cardIcon-PaintRewards-Gold.svg';
  } else {
    paintImage = 'ProTiers/cardIcon-PaintRewards-Member.svg';
  }
  return (
    <div className="notification-block">
      <a
        className="link-url"
        href={getHostCURL(`${programDetailsPrefix}/proxtraprogramdetails`)}
        rel=" noreferrer"
        onClick={() => handleTileAnalytics(analytics.PREFIX, analytics.pricingUnlocked)}
      >
        <div className="notification-image">
          <Image
            // className="notification-image"
            src={getImageURL(paintImage)}
            width="23px"
            height="28px"
            alt="paintImage"
          />
        </div>
        <div className="notification-label">
          <PaintMemberLabel
            currentPaintLevel={currentPaintLevel}
            paintCurrentYearSpend={paintCurrentYearSpend}
          />
        </div>
        <div className="link-label">
          <Image
            width="10px"
            height="16px"
            src={getImageURL('caret-small.svg')}
            alt="Orangee Arrow"
            className="link-arrow"
          />
        </div>
      </a>
    </div>
  );
};

const PerkStatusUnavailable = ({ error: err, isLoadingPerks, refreshAvailablePerks }) => {
  const refreshLoading = !isLoadingPerks;
  return (
    <div className="notification-block">
      <div className="link-url">
        <div className="notification-image">
          <Image
            // className="notification-image"
            width="25px"
            height="30px"
            src={getImageURL('Pro-Xtra_Perk_Icon-whiteFill.svg')}
            alt="paintImage"
          />
        </div>
        <div className="notification-label">
          {refreshLoading ? (
            'Perks Status Unavailable'
          ) : (
            <span className="loadingLabel">Loading Perks Status</span>
          )}
        </div>
        <div className="link-label-text">
          {refreshLoading ? (
            <div aria-hidden="true" onClick={refreshAvailablePerks}>
              Refresh
            </div>
          ) : (
            <Loader active small />
          )}
        </div>
      </div>
    </div>
  );
};
const OfferStatusUnavailable = ({ error: err, isLoadingPerks, refreshAvailableOffers }) => {
  const refreshLoading = !isLoadingPerks;
  return (
    <div className="notification-block">
      <div className="link-url">
        <div className="notification-image">
          <Image
            // className="notification-image"
            width="25px"
            height="30px"
            src={getImageURL('Pro-Xtra_Perk_Icon-whiteFill.svg')}
            alt="paintImage"
          />
        </div>
        <div className="notification-label">
          {refreshLoading ? (
            'Offers Status Unavailable'
          ) : (
            <span className="loadingLabel">Loading Offers Status</span>
          )}
        </div>
        <div className="link-label-text">
          {refreshLoading ? (
            <div aria-hidden="true" onClick={refreshAvailableOffers}>
              Refresh
            </div>
          ) : (
            <Loader active small />
          )}
        </div>
      </div>
    </div>
  );
};

export const showingTileNotifications = ({ isAdmin, userData, availablePerksData }) => {
  const { customerInfo, perkSummary } = userData || {};
  const { activePerksCount, availableActiveOffers } = perkSummary || {};
  const accrualData = getAccrualData(perkSummary);
  const proXtraCCProgram = perkSummary?.pxccUserCategory === 'Enrolled';
  const showRenderPerks = isAdmin
    && !isObjectEmpty(accrualData)
    && (perkSummary?.unclaimedPerksCount > 0
      || (!accrualData?.finalTierPerkAchieved && proXtraCCProgram)
      || accrualData?.finalTierPerkAchieved
      || availablePerksData !== undefined
      || activePerksCount);

  const showEnrollmentPrograms = customerInfo?.enrolledInPreferredPricing && !customerInfo?.preferredPricingTierZero;

  const showingTile = {
    RenderPerksProgress: showRenderPerks,
    // PerkExclusive: showEnrollmentPrograms,
    EnrollmentPrograms: true,
  };
  return showingTile;
};

export const prioritizedListNotifications = ({
  showingTile,
  userData,
  availablePerksData,
  isLoadingPerks,
  refreshAvailablePerks,
  refreshAvailableOffers,
  availablePerksError,
}) => {
  const { customerInfo, currentYearMemberships, perkSummary, userProfile } = userData || {};
  const proXtraCCProgram = perkSummary?.pxccUserCategory === 'Enrolled';
  const activeSurpriseDelightCount = perkSummary?.activeSurpriseDelightCount || 0;
  const { activePerksCount, availableActiveOffers } = perkSummary || {};
  const accrualData = getAccrualData(perkSummary);
  let currentProPaintRewards;
  const currentPaintLevel = customerInfo?.paintMembershipLevel
    ? customerInfo?.paintMembershipLevel
    : 'Member';
  const paintSpend = () => {
    if (currentYearMemberships) {
      const mem = currentYearMemberships?.currentYearMembershipInfo;
      mem.map((current) => {
        if (current.programId === '2') {
          currentProPaintRewards = current;
        }
        return currentProPaintRewards;
      });
    }
  };
  const RenderPerksProgress = () => {
    const finalTierPerkAchieved = accrualData[0]?.finalTierPerkAchieved;
    const { unclaimedPerksCount } = perkSummary || {};
    const data = availablePerksData;
    const error = !availablePerksData || isObjectEmpty(availablePerksData) || availablePerksError;
    let availablePerksCount;
    if (finalTierPerkAchieved && !activePerksCount && unclaimedPerksCount < 1) {
      return <WellDoneCard userProfile={userProfile} />;
    }

    if (activePerksCount && (isLoadingPerks || error)) {
      return (
        <PerkStatusUnavailable
          isLoadingPerks={isLoadingPerks}
          refreshAvailablePerks={refreshAvailablePerks}
        />
      );
    }

    // State 5
    if (activePerksCount && !isLoadingPerks && data) {
      availablePerksCount = data;
      if (availablePerksCount?.expiringPerks > 0) {
        return <PerkExpire availablePerksCount={availablePerksCount} />;
      }
    }

    // State 4
    if (unclaimedPerksCount > 0) {
      return <UnlockPerk perkSummary={perkSummary} />;
    }

    // State 3
    if (activePerksCount && !isLoadingPerks && data) {
      if (availablePerksCount?.availablePerks > 0) {
        return <AvailablePerks availablePerksCount={availablePerksCount} />;
      }
    }

    // State 2
    if (!finalTierPerkAchieved && proXtraCCProgram) {
      return <ProCreditCard perkSummary={perkSummary} />;
    }

    // State 1
    if (!finalTierPerkAchieved && !proXtraCCProgram) {
      return <ProgressPerk perkSummary={perkSummary} />;
    }

    return (
      <PerkStatusUnavailable
        error={error}
        isLoadingPerks={isLoadingPerks}
        refreshAvailablePerks={refreshAvailablePerks}
      />
    );
  };

  const EnrollmentPrograms = () => {
    paintSpend();
    const paintCurrentYearSpend = currentProPaintRewards?.currentYearSpend;
    const enrolledInPerks = customerInfo?.enrolledInPerksProgram;
    const data = availablePerksData;
    const error = !availablePerksData || isObjectEmpty(availablePerksData) || availablePerksError;
    let availablePerksCount;
    if (!availableActiveOffers && !customerInfo?.enrolledInPreferredPricing) {
      return (
        <PaintMemberProducts
          currentPaintLevel={currentPaintLevel}
          paintCurrentYearSpend={paintCurrentYearSpend}
        />
      );
    }

    if (!availableActiveOffers
      && customerInfo?.enrolledInPreferredPricing && !customerInfo?.preferredPricingTierZero) {
      return <PreferredUnlocked />;
    }
    if (enrolledInPerks && availableActiveOffers && (isLoadingPerks || error)) {
      return (
        <OfferStatusUnavailable
          isLoadingPerks={isLoadingPerks}
          refreshAvailableOffers={refreshAvailableOffers}
        />
      );
    }
    if (enrolledInPerks && availableActiveOffers && !isLoadingPerks && data) {
      availablePerksCount = data;
      if (availablePerksCount?.availableOffers > 0) {
        return <PerkExclusive availablePerksCount={availablePerksCount} />;
      }
    }
    return (
      <PaintMemberProducts
        currentPaintLevel={currentPaintLevel}
        paintCurrentYearSpend={paintCurrentYearSpend}
      />
    );
  };

  const prioritizedList = [
    showingTile.RenderPerksProgress && <RenderPerksProgress />,
    // showingTile.PerkExclusive && <PerkExclusive />,
    showingTile.EnrollmentPrograms && <EnrollmentPrograms />,
  ];

  return prioritizedList;
};

const NotificationSection = ({ tiles }) => {
  const DisplayTiles = () => tiles;

  return <DisplayTiles />;
};

export const exportsForTesting = {
  ProgressPerk,
  UnlockPerk,
  AvailablePerks,
  WellDoneCard,
  PerkExpire,
  PreferredUnlocked,
  ProCreditCard,
  PaintMemberProducts,
  PerkStatusUnavailable,
  PerkExclusive,
};

export default NotificationSection;
